import { useState } from 'react';
import { Badge } from '@localagent/ui/components/badge';
import { Button } from '@localagent/ui/components/button';
import { Input } from '@localagent/ui/components/input';
import type { SupportedParamSpec } from '@localagent/sdk';

const NUMERIC_TYPES = ['numeric', 'integer'];

function isNumericType(type: string): boolean {
  return NUMERIC_TYPES.includes(type);
}

function parseNumericInput(raw: string): number | null {
  if (raw.trim() === '') return null;
  const num = Number(raw);
  return Number.isNaN(num) ? null : num;
}

/** A newly authored key starts as an unbounded numeric — the author fills bounds. */
const NEW_PARAM_SPEC: SupportedParamSpec = {
  type: 'numeric',
  label: '',
  ui_hint: 'slider',
  sort_order: 50,
};

export interface ParamSpecEditorProps {
  /** Params at the level being edited (entry-level or provider-level). */
  params: Record<string, SupportedParamSpec>;
  /** Merged provider + entry params (display context for fallback rows). */
  effectiveParams?: Record<string, SupportedParamSpec>;
  /**
   * Provider-level definitions. When present, removing a param that also
   * exists here shows a "falls back to provider default" hint row.
   */
  providerParams?: Record<string, SupportedParamSpec>;
  onChange: (params: Record<string, SupportedParamSpec>) => void;
  disabled?: boolean;
  /**
   * Server rejection shown after a save that narrowed a bound below a pinned
   * agent value. Rendered verbatim — the client composes none.
   */
  narrowingError?: string;
}

/**
 * Structured per-param field editor for `Record<string, SupportedParamSpec>`.
 * No raw JSON surface — each param renders as typed fields (default value,
 * min/max/step for numeric types, enum select for enums).
 *
 * A member can ADD a param key the discovery spec did not declare.
 */
export function ParamSpecEditor({
  params,
  effectiveParams,
  providerParams,
  onChange,
  disabled,
  narrowingError,
}: ParamSpecEditorProps) {
  const [newKey, setNewKey] = useState('');
  const [addError, setAddError] = useState('');

  const sortedKeys = Object.keys(params).sort((a, b) => {
    const orderDiff = (params[a].sort_order ?? 0) - (params[b].sort_order ?? 0);
    return orderDiff !== 0 ? orderDiff : a.localeCompare(b);
  });

  // Keys defined at the provider level but absent at this level — they fall
  // back to the provider default. Rendered as informational rows so a
  // just-removed param visibly lands in "falls back" state.
  const fallbackKeys = providerParams
    ? Object.keys(providerParams).filter((key) => !(key in params)).sort()
    : [];

  const updateSpec = (key: string, patch: Partial<SupportedParamSpec>) => {
    onChange({ ...params, [key]: { ...params[key], ...patch } });
  };

  const removeParam = (key: string) => {
    const next = { ...params };
    delete next[key];
    onChange(next);
  };

  const addParam = () => {
    const key = newKey.trim();
    setAddError('');
    if (!key) {
      setAddError('Enter a parameter key');
      return;
    }
    if (!/^[a-z][a-z0-9_]*$/.test(key)) {
      setAddError('Key must be snake_case starting with a letter');
      return;
    }
    if (key in params) {
      setAddError(`Parameter "${key}" already exists`);
      return;
    }
    onChange({
      ...params,
      [key]: { ...NEW_PARAM_SPEC, label: key },
    });
    setNewKey('');
  };

  return (
    <div className="flex flex-col gap-2">
      {sortedKeys.length === 0 && fallbackKeys.length === 0 && (
        <p className="text-caption text-muted-foreground">No parameters defined.</p>
      )}
      {sortedKeys.map((key) => {
        const spec = params[key];
        const numeric = isNumericType(spec.type);
        const isEnum = spec.type === 'enum' && (spec.values?.length ?? 0) > 0;
        return (
          <div key={key} className="rounded-md bg-card p-2" data-testid={`param-row-${key}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-label font-medium truncate">{spec.label || key}</span>
                <span className="font-mono text-caption text-muted-foreground">{key}</span>
                <Badge variant="outline" className="text-micro">{spec.type}</Badge>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 shrink-0 text-destructive hover:text-destructive"
                data-testid={`param-remove-${key}`}
                title={providerParams && key in providerParams
                  ? 'Remove — falls back to provider default'
                  : 'Remove parameter'}
                disabled={disabled}
                onClick={() => removeParam(key)}
              >
                x
              </Button>
            </div>
            <div className="mt-2 grid grid-cols-4 gap-2">
              <div className="col-span-1">
                <label className="mb-1 block text-caption text-muted-foreground">Default</label>
                {isEnum ? (
                  <select
                    data-testid={`param-default-${key}`}
                    value={spec.default_value != null ? String(spec.default_value) : ''}
                    disabled={disabled}
                    onChange={(e) => updateSpec(key, { default_value: e.target.value })}
                    className="w-full h-8 rounded-md bg-muted px-2 text-caption focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                  >
                    {(spec.values ?? []).map((value, i) => (
                      <option key={value} value={value}>
                        {spec.labels?.[i] ?? value}
                      </option>
                    ))}
                  </select>
                ) : numeric ? (
                  <Input
                    type="number"
                    data-testid={`param-default-${key}`}
                    value={spec.default_value != null ? String(spec.default_value) : ''}
                    disabled={disabled}
                    onChange={(e) => updateSpec(key, { default_value: parseNumericInput(e.target.value) })}
                    className="h-8 text-caption"
                  />
                ) : (
                  <Input
                    data-testid={`param-default-${key}`}
                    value={spec.default_value != null ? String(spec.default_value) : ''}
                    disabled={disabled}
                    onChange={(e) => updateSpec(key, { default_value: e.target.value })}
                    className="h-8 text-caption"
                  />
                )}
              </div>
              {numeric && (
                <>
                  <div>
                    <label className="mb-1 block text-caption text-muted-foreground">Min</label>
                    <Input
                      type="number"
                      data-testid={`param-min-${key}`}
                      value={spec.min != null ? String(spec.min) : ''}
                      disabled={disabled}
                      onChange={(e) => updateSpec(key, { min: parseNumericInput(e.target.value) })}
                      className="h-8 text-caption"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-caption text-muted-foreground">Max</label>
                    <Input
                      type="number"
                      data-testid={`param-max-${key}`}
                      value={spec.max != null ? String(spec.max) : ''}
                      disabled={disabled}
                      onChange={(e) => updateSpec(key, { max: parseNumericInput(e.target.value) })}
                      className="h-8 text-caption"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-caption text-muted-foreground">Step</label>
                    <Input
                      type="number"
                      data-testid={`param-step-${key}`}
                      value={spec.step != null ? String(spec.step) : ''}
                      disabled={disabled}
                      onChange={(e) => updateSpec(key, { step: parseNumericInput(e.target.value) })}
                      className="h-8 text-caption"
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })}
      {fallbackKeys.map((key) => {
        const spec = providerParams![key];
        const effectiveDefault = effectiveParams?.[key]?.default_value ?? spec.default_value;
        return (
          <div
            key={key}
            className="flex items-center justify-between gap-2 rounded-md p-2"
            data-testid={`param-fallback-hint-${key}`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-body text-muted-foreground truncate">{spec.label || key}</span>
              <span className="font-mono text-caption text-muted-foreground">{key}</span>
            </div>
            <span className="text-caption text-muted-foreground shrink-0">
              Falls back to provider default
              {effectiveDefault != null ? ` (${String(effectiveDefault)})` : ''}
            </span>
          </div>
        );
      })}

      {/* Add a param key discovery did not declare. */}
      <div
        data-testid="param-spec-add-key"
        className="flex flex-col gap-1 rounded-md p-2"
      >
        <div className="flex items-center gap-2">
          <Input
            data-testid="param-spec-add-key-input"
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
            disabled={disabled}
            placeholder="new_param_key"
            className="h-8 text-caption font-mono"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 shrink-0 text-caption"
            data-testid="param-spec-add-key-button"
            disabled={disabled}
            onClick={addParam}
          >
            + Add parameter
          </Button>
        </div>
        {addError && (
          <p data-testid="param-spec-add-key-error" className="text-caption text-destructive">
            {addError}
          </p>
        )}
      </div>

      {narrowingError && (
        <p
          data-testid="param-spec-narrowing-error"
          className="rounded-md bg-destructive/10 p-2 text-caption text-destructive"
        >
          {narrowingError}
        </p>
      )}
    </div>
  );
}
